import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl } from '../../baseurl';
import { 
  ArrowLeft, FolderOpen, MapPin, Search, Database, 
  HardDrive, ChevronDown, ClipboardList 
} from 'lucide-react';

const BulkFolderUpload = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const adminToken = localStorage.getItem('adminToken');

  // State Management
  const [rootFolderName, setRootFolderName] = useState("");
  const [projects, setProjects] = useState({}); 
  const [isScanning, setIsScanning] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState("");

  // Fetch Service Packages on Component Mount
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const res = await axios.get(`${baseurl}getAllServicePackages`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        if (res.data.success) {
          setPackages(res.data.data);
        }
      } catch (err) { 
        console.error("Packages load fail", err); 
      }
    };
    fetchPackages();
  }, [adminToken]);

  const resetStates = () => {
    setProjects({});
    setRootFolderName("");
    setSearchTerm("");
    setSelectedPackageId("");
  };

  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsScanning(true);
    
    // Set the Main Bulk Title from the root folder name
    const rootName = files[0].webkitRelativePath.split('/')[0];
    setRootFolderName(rootName);

    const batchData = {};

    files.forEach(file => {
      const pathParts = file.webkitRelativePath.split('/');
      
      // Structure: Root(path[0]) / Address(path[1]) / file(path[2])
      if (pathParts.length < 3) return; 

      const addressName = pathParts[1];
      const fileName = file.name.toLowerCase();

      // Only process .geojson or .json
      if (!fileName.endsWith('.geojson') && !fileName.endsWith('.json')) return;

      if (!batchData[addressName]) {
        batchData[addressName] = {
          address: addressName,
          parcel: null,
          zone: null,
          others: [],
          isValid: false
        };
      }

      if (fileName.includes('parcel')) {
        batchData[addressName].parcel = file;
        batchData[addressName].isValid = true;
      } else if (fileName.includes('zone')) {
        batchData[addressName].zone = file;
      } else {
        batchData[addressName].others.push(file);
      }
    });

    setProjects(batchData);
    setIsScanning(false);
  };

  const projectKeys = Object.keys(projects).filter(k => 
    k.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalValid = Object.values(projects).filter(p => p.isValid).length;

  const handleBatchSubmit = async () => {
    if (totalValid === 0) return;

    if (!selectedPackageId) {
        Swal.fire('Selection Required', 'Please choose a Report Type (Package) before saving.', 'warning');
        return;
    }

    const formData = new FormData();
    formData.append('bulk_title', rootFolderName);
    formData.append('total_projects', totalValid);
    formData.append('userId', userId); 
    formData.append('reportType', selectedPackageId);

    const validProjects = Object.values(projects).filter(p => p.isValid);

    validProjects.forEach((p, index) => {
        formData.append(`address_${index}`, p.address);
        formData.append(`parcel_${index}`, p.parcel);
        
        if (p.zone) {
            formData.append(`zone_${index}`, p.zone);
        }

        p.others.forEach((layer, lIdx) => {
            formData.append(`others_${index}_${lIdx}`, layer);
        });
    });

    Swal.fire({
        title: 'Confirm Bulk Upload',
        html: `Ready to upload <b>${totalValid}</b> projects for User ID: <b>${userId}</b>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#4318FF',
        confirmButtonText: 'Start Upload'
    }).then(async (result) => {
        if (result.isConfirmed) {
            try {
                Swal.fire({
                    title: 'Processing...',
                    text: 'Uploading files and calculating areas...',
                    allowOutsideClick: false,
                    didOpen: () => Swal.showLoading()
                });

                const res = await axios.post(`${baseurl}processBulkUpload`, formData, { 
                    headers: { 
                        'Authorization': `Bearer ${adminToken}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                });

                if (res.data.success) {
                    Swal.fire('Success', 'Batch uploaded successfully!', 'success');
                    resetStates(); 
                }
            } catch (err) {
                console.error("Upload Error:", err);
                Swal.fire('Error', err.response?.data?.message || 'Failed to upload batch.', 'error');
            }
        }
    });
  };

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', minHeight: '100vh' }}>
        <div className="container-fluid p-4">
          
          {/* Header Section */}
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
            <div className="d-flex align-items-center">
              <button onClick={() => navigate(-1)} className="btn btn-white shadow-sm rounded-circle me-3">
                <ArrowLeft size={20} />
              </button>
              <div>
                <h3 className="fw-bold m-0" style={{ color: '#1B2559' }}>Batch Upload Manager</h3>
                <p className="text-muted small m-0">Group: <span className="fw-bold text-primary">{rootFolderName || "Not Selected"}</span></p>
              </div>
            </div>

            <div className="d-flex gap-2 align-items-center">
              {/* Report Type Selector */}
              <div className="position-relative">
                <ClipboardList className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
                <select 
                  className="form-select rounded-pill border-0 shadow-sm ps-5 pe-4"
                  style={{ minWidth: '220px', height: '45px', cursor: 'pointer', appearance: 'none' }}
                  value={selectedPackageId}
                  onChange={(e) => setSelectedPackageId(e.target.value)}
                >
                  <option value="">Select Report Type...</option>
                  {packages.map((pkg) => (
                    <option key={pkg.id} value={pkg.name}>{pkg.name}</option>
                  ))}
                </select>
                <ChevronDown className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted pointer-events-none" size={14} />
              </div>

              {/* Folder Selector */}
              <label className="btn btn-primary rounded-pill px-4 shadow d-flex align-items-center" style={{ cursor: 'pointer', height: '45px' }}>
                <FolderOpen size={18} className="me-2" /> 
                {rootFolderName ? 'Change Folder' : 'Select Folder'}
                <input type="file" webkitdirectory="true" directory="true" hidden onChange={handleFolderSelect} />
              </label>

              {totalValid > 0 && (
                <button className="btn btn-success rounded-pill px-4 shadow d-flex align-items-center" onClick={handleBatchSubmit} style={{ height: '45px' }}>
                  <Database size={18} className="me-2" /> Save to DB
                </button>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              {Object.keys(projects).length === 0 ? (
                <div className="card border-0 shadow-sm p-5 text-center bg-white" style={{ borderRadius: '24px' }}>
                  <HardDrive size={60} className="text-muted mb-3 mx-auto opacity-25" />
                  <h4 style={{ color: '#1B2559' }}>No Folder Selected</h4>
                  <p className="text-muted mx-auto" style={{ maxWidth: '400px' }}>
                    Select a root folder containing sub-folders. Each sub-folder should contain a <b>parcel</b> GeoJSON file.
                  </p>
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="position-relative w-25">
                      <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={16} />
                      <input 
                        type="text" 
                        className="form-control rounded-pill ps-5 border-0 shadow-sm" 
                        placeholder="Search addresses..." 
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="d-flex gap-2">
                        <div className="badge bg-white text-dark shadow-sm p-2 px-3 rounded-pill border">
                           Total: <span className="text-primary fw-bold">{Object.keys(projects).length}</span>
                        </div>
                        <div className="badge bg-white text-dark shadow-sm p-2 px-3 rounded-pill border">
                           Valid: <span className="text-success fw-bold">{totalValid}</span>
                        </div>
                    </div>
                  </div>

                  <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '20px' }}>
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light">
                          <tr>
                            <th className="ps-4">Address (Sub-folder)</th>
                            <th>Parcel</th>
                            <th>Zone</th>
                            <th>Features</th>
                            <th className="text-end pe-4">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectKeys.map((key) => {
                            const p = projects[key];
                            return (
                              <tr key={key}>
                                <td className="ps-4">
                                  <div className="d-flex align-items-center">
                                    <div className="bg-primary-soft p-2 rounded-3 me-3 text-primary">
                                      <MapPin size={18} />
                                    </div>
                                    <span className="fw-bold">{p.address}</span>
                                  </div>
                                </td>
                                <td>{p.parcel ? <span className="text-success small fw-bold">✓ Detected</span> : <span className="text-danger small">✗ Missing</span>}</td>
                                <td>{p.zone ? <span className="text-primary small">✓ Detected</span> : <span className="text-muted small">N/A</span>}</td>
                                <td><span className="badge bg-light text-dark border">{p.others.length} Layers</span></td>
                                <td className="text-end pe-4">
                                  {p.isValid ? (
                                    <span className="badge bg-success-soft text-success rounded-pill px-3">Ready</span>
                                  ) : (
                                    <span className="badge bg-danger-soft text-danger rounded-pill px-3">Incomplete</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .bg-primary-soft { background: #EBEBFF; color: #4318FF; }
        .bg-success-soft { background: #E6F7ED; color: #10B981; }
        .bg-danger-soft { background: #FFEEF3; color: #FF5B5B; }
        .btn-white { background: white; border: none; color: #1B2559; }
        .table thead th { font-size: 11px; text-transform: uppercase; color: #A3AED0; padding: 15px; border: none; }
        .table tbody td { border-top: 1px solid #F4F7FE; padding: 15px; color: #2B3674; font-size: 14px; }
        .form-select:focus { box-shadow: 0 4px 12px rgba(0,0,0,0.05); border: none; }
        .pointer-events-none { pointer-events: none; }
      `}</style>
    </div>
  );
};

export default BulkFolderUpload;